import type * as CookieConsent from 'vanilla-cookieconsent';

import {
  COOKIE_CONSENT_OPTIONAL_CATEGORIES,
  type CookieConsentCategory,
  type CookieConsentOptionalCategory,
  type CookieConsentRegion,
  getCookieConsentCookieDomain,
  getCookieConsentCookieName,
  readExplicitCookieConsentCategory,
  resolveCookieConsentDeployment,
  resolveRegionalCookieConsentMode,
} from '@repo/common';

import { clearMarketingRetentionStorage } from '../utils/marketing-storage';
import { COOKIE_CONSENT_ENABLED, COOKIE_CONSENT_REVISION } from './cookie-consent-config';

export interface ConsentState {
  analytics: boolean;
  external_media: boolean;
  hasDecision: boolean;
  marketing: boolean;
  necessary: true;
}

type ConsentValue = 'denied' | 'granted';
export type ConsentListener = () => void;
/** Which surface of the StartHub-rendered consent UI is open. The library's own UI is never shown. */
export type CookieConsentView = 'banner' | 'hidden' | 'preferences';
export type OptionalConsentChoices = Record<CookieConsentOptionalCategory, boolean>;

interface ConsentModeState {
  ad_personalization: ConsentValue;
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  analytics_storage: ConsentValue;
}

interface ClarityCommand {
  (...args: unknown[]): void;
  q?: unknown[][];
}

declare global {
  interface Window {
    clarity?: ClarityCommand;
    dataLayer?: unknown[][];
    gtag?: (...args: unknown[]) => void;
  }
}

const BOT_USER_AGENT_PATTERN = /bot|crawl|spider|slurp|teoma/i;
const TRACKING_COOKIE_PRODUCTION_HOSTS = new Set([
  'admin.starthub.academy',
  'founders.starthub.academy',
  'partner.starthub.academy',
  'starthub.academy',
  'www.starthub.academy',
]);
const ANALYTICS_COOKIE_NAMES = [/^_ga/, '_gid', /^_clc/, '_clck', '_clsk', 'ANONCHK', 'CLID', 'MR', 'MUID', 'SM'];
const CLARITY_SESSION_STORAGE_KEY = '_cltk';
const EXTERNAL_MEDIA_COOKIE_NAMES = ['vuid', /^player/];
const MARKETING_COOKIE_NAMES = ['hubspotutk', '__hstc', '__hssc', '__hssrc', 'messagesUtk', '_fbp', '_fbc'];
const CONSENT_CHANGE_KEYS = ['analytics', 'external_media', 'hasDecision', 'marketing'] as const;
const INITIAL_CONSENT_STATE: ConsentState = {
  analytics: !COOKIE_CONSENT_ENABLED,
  external_media: !COOKIE_CONSENT_ENABLED,
  hasDecision: !COOKIE_CONSENT_ENABLED,
  marketing: !COOKIE_CONSENT_ENABLED,
  necessary: true,
};
const consentListeners = new Set<ConsentListener>();
const consentViewListeners = new Set<ConsentListener>();

let consentState = INITIAL_CONSENT_STATE;
let consentView: CookieConsentView = 'hidden';
let consentResolved = false;
let initializationPromise: Promise<void> | undefined;
let consentModeDefaultsSet = false;
let regionalMode: 'opt-in' | 'opt-out' = 'opt-in';
let libraryConsentCookie: string | undefined;
let libraryRefresh: Promise<void> | undefined;
let libraryModule: typeof CookieConsent | undefined;
let libraryLoad: Promise<typeof CookieConsent> | undefined;
let libraryRan = false;

/**
 * The library only stores consent and clears cookies, so it loads on demand. Until it arrives, consent is
 * answered from the consent cookie itself (see readConsentState), which is the library's only source of truth.
 */
function loadCookieConsentLibrary(): Promise<typeof CookieConsent> {
  libraryLoad =
    libraryLoad ??
    import('vanilla-cookieconsent').then(module => {
      libraryModule = module;
      return module;
    });
  return libraryLoad;
}

/** Starts fetching the library without waiting for it, so initialization does not pay for the download later. */
export function preloadCookieConsentLibrary(): void {
  if (COOKIE_CONSENT_ENABLED && isBrowser()) {
    void loadCookieConsentLibrary().catch(() => undefined);
  }
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

export function hasGlobalPrivacyControl(): boolean {
  return isBrowser() && Reflect.get(navigator, 'globalPrivacyControl') === true;
}

function getTrackingCookieDomain(): string | undefined {
  if (!isBrowser()) {
    return undefined;
  }

  return TRACKING_COOKIE_PRODUCTION_HOSTS.has(window.location.hostname) ? '.starthub.academy' : undefined;
}

function getCookieVariants(name: string | RegExp): CookieConsent.CookieItem[] {
  const domain = getTrackingCookieDomain();
  return domain ? [{ name }, { name, domain }] : [{ name }];
}

function getConsentModeState(state: ConsentState): ConsentModeState {
  const analyticsStorage = state.analytics ? 'granted' : 'denied';
  const adStorage = state.marketing ? 'granted' : 'denied';

  return {
    ad_personalization: adStorage,
    ad_storage: adStorage,
    ad_user_data: adStorage,
    analytics_storage: analyticsStorage,
  };
}

export function initializeGoogleConsentMode(): void {
  if (!(COOKIE_CONSENT_ENABLED && isBrowser()) || consentModeDefaultsSet) {
    return;
  }

  window.dataLayer = window.dataLayer ?? [];
  window.gtag =
    window.gtag ??
    ((...args: unknown[]) => {
      window.dataLayer?.push(args);
    });
  window.gtag('consent', 'default', {
    ad_personalization: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    analytics_storage: 'denied',
    wait_for_update: 500,
  });
  consentModeDefaultsSet = true;
}

export function syncTrackingConsentSignals(): void {
  if (!(COOKIE_CONSENT_ENABLED && isBrowser())) {
    return;
  }

  initializeGoogleConsentMode();
  const consentModeState = getConsentModeState(consentState);
  window.gtag?.('consent', 'update', consentModeState);
  window.clarity?.('consentv2', {
    ad_Storage: consentModeState.ad_storage,
    analytics_Storage: consentModeState.analytics_storage,
  });
}

function notifyConsentListeners(): void {
  for (const listener of consentListeners) {
    listener();
  }
}

function setConsentState(nextState: ConsentState): void {
  const previousState = consentState;
  consentState = nextState;
  syncTrackingConsentSignals();

  revokeTrackingConsentIfWithdrawn(previousState, nextState);

  if (CONSENT_CHANGE_KEYS.some(key => previousState[key] !== nextState[key])) {
    notifyConsentListeners();
  }
}

function revokeTrackingConsentIfWithdrawn(previousState: ConsentState, nextState: ConsentState): void {
  if (!previousState.marketing && nextState.marketing) {
    window.fbq?.('consent', 'grant');
  }
  if (previousState.marketing && !nextState.marketing) {
    window.fbq?.('consent', 'revoke');
  }

  if (previousState.analytics && !nextState.analytics) {
    window.clarity?.('consent', false);
    window.clarity?.('stop');
  }
}

function readConsentState(): ConsentState {
  const raw = readStoredConsentCookie();
  const hasDecision = readExplicitCookieConsentCategory(raw, 'necessary') === true;
  const allowDefaults = regionalMode === 'opt-out' && raw === undefined;

  return {
    analytics: hasDecision ? readExplicitCookieConsentCategory(raw, 'analytics') === true : allowDefaults,
    external_media: hasDecision ? readExplicitCookieConsentCategory(raw, 'external_media') === true : allowDefaults,
    hasDecision,
    marketing:
      !hasGlobalPrivacyControl() &&
      (hasDecision ? readExplicitCookieConsentCategory(raw, 'marketing') === true : allowDefaults),
    necessary: true,
  };
}

export function syncConsentState(): void {
  const nextState = readConsentState();
  clearRejectedCategoryCookies(nextState);
  setConsentState(nextState);
}

/** Refresh library caches without writing a new decision or extending consent expiry. */
export function refreshStoredCookieConsent(): Promise<void> {
  syncConsentState();
  if (libraryRefresh) return libraryRefresh;
  if (readStoredConsentCookie() === libraryConsentCookie) return Promise.resolve();
  libraryRefresh = loadCookieConsentLibrary()
    .then(library => {
      library.reset(false);
      return library.run(getCookieConsentConfig());
    })
    .then(() => {
      libraryRan = true;
      libraryConsentCookie = readStoredConsentCookie();
      syncConsentState();
    })
    .finally(() => {
      libraryRefresh = undefined;
    });
  return libraryRefresh;
}

function saveConsentCategories(categories: string | string[]): void {
  const save = (library: typeof CookieConsent) => {
    library.acceptCategory(categories);
    libraryConsentCookie = readStoredConsentCookie();
    syncConsentState();
  };
  if (libraryModule && libraryRan && !libraryRefresh && readStoredConsentCookie() === libraryConsentCookie) {
    save(libraryModule);
  } else {
    // The footer can open the preferences before the library has loaded; wait for it, then save.
    void (initializationPromise ?? Promise.resolve())
      .then(refreshStoredCookieConsent)
      .then(loadCookieConsentLibrary)
      .then(save);
  }
}

function readStoredConsentCookie(): string | undefined {
  const name = getCookieConsentCookieName(window.location.hostname);
  const cookie = document.cookie
    .split(';')
    .map(value => value.trim())
    .find(value => value.startsWith(`${name}=`));
  if (cookie === undefined) return undefined;
  try {
    return decodeURIComponent(cookie.slice(name.length + 1));
  } catch {
    return ''; // Present but invalid: never fall back to an automatic grant.
  }
}

/** Erasing needs the library; a state synced before it loads is cleared by the sync that follows its load. */
function clearCookies(names: (string | RegExp)[]): void {
  if (!libraryModule) return;
  const domain = getTrackingCookieDomain();
  libraryModule.eraseCookies(names, '/');

  if (domain) {
    libraryModule.eraseCookies(names, '/', domain);
  }
}

function clearRejectedCategoryCookies(state: ConsentState): void {
  if (!state.analytics) {
    clearCookies(ANALYTICS_COOKIE_NAMES);
    try {
      sessionStorage.removeItem(CLARITY_SESSION_STORAGE_KEY);
    } catch {
      // Consent withdrawal must remain usable when session storage is denied.
    }
  }

  if (!state.external_media) {
    clearCookies(EXTERNAL_MEDIA_COOKIE_NAMES);
  }

  if (!state.marketing) {
    clearCookies(MARKETING_COOKIE_NAMES);
    clearMarketingRetentionStorage();
  }
}

export function subscribeToConsent(listener: ConsentListener): () => void {
  consentListeners.add(listener);
  return () => consentListeners.delete(listener);
}

export function getConsentSnapshot(): ConsentState {
  return consentState;
}

export function getServerConsentSnapshot(): ConsentState {
  return INITIAL_CONSENT_STATE;
}

export function subscribeToConsentView(listener: ConsentListener): () => void {
  consentViewListeners.add(listener);
  return () => consentViewListeners.delete(listener);
}

export function getConsentViewSnapshot(): CookieConsentView {
  return consentView;
}

export function getServerConsentViewSnapshot(): CookieConsentView {
  return 'hidden';
}

/** Whether the banner decision has been made: until initialization ends, a hidden view only means not yet shown. */
export function getConsentResolvedSnapshot(): boolean {
  return consentResolved || !COOKIE_CONSENT_ENABLED;
}

export function getServerConsentResolvedSnapshot(): boolean {
  return false;
}

function markConsentResolved(): void {
  if (consentResolved) {
    return;
  }

  consentResolved = true;
  for (const listener of consentViewListeners) {
    listener();
  }
}

function setConsentView(nextView: CookieConsentView): void {
  if (consentView === nextView) {
    return;
  }

  consentView = nextView;
  for (const listener of consentViewListeners) {
    listener();
  }
}

/**
 * The only bot check for consent. The library's own `hideFromBots` is turned off in the config, so it
 * always runs and StartHub alone decides whether crawlers see the banner.
 */
function isLikelyBot(): boolean {
  return BOT_USER_AGENT_PATTERN.test(navigator.userAgent) || navigator.webdriver;
}

function getCookieConsentConfig(): CookieConsent.CookieConsentConfig {
  const hostname = window.location.hostname;
  const cookieDomain = getCookieConsentCookieDomain(hostname);

  return {
    autoClearCookies: true,
    categories: {
      analytics: {
        enabled: regionalMode === 'opt-out',
        autoClear: {
          cookies: [...ANALYTICS_COOKIE_NAMES.flatMap(getCookieVariants)],
        },
      },
      external_media: {
        enabled: regionalMode === 'opt-out',
        autoClear: {
          cookies: EXTERNAL_MEDIA_COOKIE_NAMES.flatMap(getCookieVariants),
        },
      },
      marketing: {
        enabled: regionalMode === 'opt-out' && !hasGlobalPrivacyControl(),
        autoClear: {
          cookies: [...MARKETING_COOKIE_NAMES.flatMap(getCookieVariants)],
        },
      },
      necessary: {
        enabled: true,
        readOnly: true,
      },
    },
    cookie: {
      expiresAfterDays: 182,
      name: getCookieConsentCookieName(hostname),
      path: '/',
      sameSite: 'Lax',
      secure: cookieDomain !== undefined,
      ...(cookieDomain ? { domain: cookieDomain } : {}),
    },
    // The library only stores consent and clears cookies; StartHub renders the banner itself.
    autoShow: false,
    hideFromBots: false,
    language: {
      default: 'en',
      translations: {
        en: { consentModal: {}, preferencesModal: { sections: [] } },
      },
    },
    mode: regionalMode,
    onChange: syncSavedConsentState,
    onConsent: syncSavedConsentState,
    onFirstConsent: syncSavedConsentState,
    revision: COOKIE_CONSENT_REVISION,
  };
}

function syncSavedConsentState(): void {
  libraryConsentCookie = readStoredConsentCookie();
  syncConsentState();
}

export function initializeCookieConsent(verifiedRegion?: CookieConsentRegion): Promise<void> {
  if (!(COOKIE_CONSENT_ENABLED && isBrowser())) {
    return Promise.resolve();
  }

  initializeGoogleConsentMode();
  if (!initializationPromise) {
    regionalMode = resolveRegionalCookieConsentMode(verifiedRegion);
    // Answer from the consent cookie right away: the library download must not delay the stored decision.
    syncConsentState();
  }
  initializationPromise =
    initializationPromise ??
    loadCookieConsentLibrary()
      .then(async library => {
        await library.run(getCookieConsentConfig());
        libraryRan = true;
        libraryConsentCookie = readStoredConsentCookie();
        // HubSpot is retired; clear its remaining first-party cookies even for existing grants.
        clearCookies(['hubspotutk', '__hstc', '__hssc', '__hssrc', 'messagesUtk']);
        syncConsentState();
        if (!(library.validConsent() || isLikelyBot())) {
          setConsentView('banner');
        }
        markConsentResolved();
      })
      .catch(() => {
        // The library could not be downloaded: the cookie-derived state stays in force, and a visitor
        // without a stored decision still gets asked, so tracking never starts without consent.
        if (!(readConsentState().hasDecision || isLikelyBot())) {
          setConsentView('banner');
        }
        markConsentResolved();
      });
  return initializationPromise;
}

export function hasCookieConsent(category: CookieConsentCategory): boolean {
  if (category === 'marketing' && hasGlobalPrivacyControl()) return false;
  if (!COOKIE_CONSENT_ENABLED && isBrowser()) {
    const cookieName = getCookieConsentCookieName(window.location.hostname);
    const rawConsent = document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith(`${cookieName}=`))
      ?.slice(cookieName.length + 1);
    if (rawConsent) {
      try {
        const explicitDecision = readExplicitCookieConsentCategory(decodeURIComponent(rawConsent), category);
        if (explicitDecision !== undefined) {
          return explicitDecision;
        }
      } catch {
        // Invalid legacy cookies do not override the temporary automatic production policy.
      }
    }
    return resolveCookieConsentDeployment(window.location.hostname) === 'production';
  }
  return consentState[category];
}

/** A production fallback or an authentication action is not permission for a new destination. */
export function hasExplicitCookieConsent(category: CookieConsentOptionalCategory): boolean {
  if (category === 'marketing' && hasGlobalPrivacyControl()) return false;
  if (!isBrowser() || navigator.doNotTrack === '1') return false;
  if (Reflect.get(window, 'doNotTrack') === '1' || Reflect.get(navigator, 'msDoNotTrack') === '1') return false;
  try {
    const name = getCookieConsentCookieName(window.location.hostname);
    const raw = document.cookie
      .split(';')
      .map(cookie => cookie.trim())
      .find(cookie => cookie.startsWith(`${name}=`));
    if (raw) {
      const choice = readExplicitCookieConsentCategory(decodeURIComponent(raw.slice(name.length + 1)), category);
      // Expired or malformed stored decisions must not turn into an automatic grant.
      return choice === true;
    }
    return regionalMode === 'opt-out' && consentState[category];
  } catch {
    return false;
  }
}

export function hasExplicitMarketingConsent(): boolean {
  return hasExplicitCookieConsent('marketing');
}

export function showCookieBanner(): void {
  setConsentView('banner');
}

export function showCookiePreferences(): void {
  if (!COOKIE_CONSENT_ENABLED) {
    return;
  }

  // The footer can be used before async app initialization finishes, or after another tab saves a choice.
  syncConsentState();
  setConsentView('preferences');
}

export function acceptAllCookies(): void {
  saveConsentCategories(hasGlobalPrivacyControl() ? ['necessary', 'analytics', 'external_media'] : 'all');
  setConsentView('hidden');
}

/** Keeps only the read-only necessary category. */
export function rejectOptionalCookies(): void {
  saveConsentCategories([]);
  setConsentView('hidden');
}

export function getOptionalConsentChoices(): OptionalConsentChoices {
  return {
    analytics: consentState.analytics,
    external_media: consentState.external_media,
    marketing: consentState.marketing,
  };
}

/**
 * The preferences view has no save button, so its toggles are saved together when it closes.
 * Saving once applies a single consent transition to all tracking subscribers.
 */
export function saveCookiePreferences(choices: OptionalConsentChoices): void {
  const isUnchanged = COOKIE_CONSENT_OPTIONAL_CATEGORIES.every(
    category => choices[category] === consentState[category],
  );
  if (!(consentState.hasDecision && isUnchanged)) {
    saveConsentCategories(
      COOKIE_CONSENT_OPTIONAL_CATEGORIES.filter(
        category => choices[category] && !(category === 'marketing' && hasGlobalPrivacyControl()),
      ),
    );
  }
  setConsentView('hidden');
}

/**
 * Dismisses the card without saving a choice. Never-answered remains distinct from rejection.
 */
export function closeCookieConsent(): void {
  setConsentView('hidden');
}

export function grantCookieConsent(category: CookieConsentOptionalCategory): void {
  if (category === 'marketing' && hasGlobalPrivacyControl()) return;
  if (!COOKIE_CONSENT_ENABLED) {
    return;
  }

  void initializeCookieConsent().then(() => {
    const current = readConsentState();
    const acceptedCategories = ['necessary', ...COOKIE_CONSENT_OPTIONAL_CATEGORIES.filter(key => current[key])];
    saveConsentCategories([...new Set([...acceptedCategories, category])]);
    // A choice made from an embed placeholder is a decision too, so the banner has nothing left to ask.
    setConsentView('hidden');
  });
}

initializeGoogleConsentMode();
